import { useEffect, useState } from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Bell, Globe, LogOut, Menu, Moon, Sun, Monitor, Home, Users, Shield, List, History, Settings, Map, LayoutDashboard, CheckSquare, Target, Building2, UsersRound, BarChart3, Handshake, Layers, Network, PanelLeftClose, PanelLeftOpen, IdCard, CalendarClock, Palmtree, CalendarCog, Clock, ClipboardClock, Gauge, ClipboardCheck, FileSignature, BookMarked, Inbox } from 'lucide-react';
import type { NotificationList } from '@mnc/contracts';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { pick } from '../i18n';
import { cn } from './ui';

/** Icon names a manifest page may use (checked by apps/api/test/consistency.test.ts); unknown names fall back to List. */
const ICONS: Record<string, typeof Home> = {
  home: Home,
  bell: Bell,
  users: Users,
  shield: Shield,
  list: List,
  history: History,
  settings: Settings,
  map: Map,
  dashboard: LayoutDashboard,
  tasks: CheckSquare,
  strategy: Target,
  stakeholders: Building2,
  beneficiaries: UsersRound,
  reports: BarChart3,
  programs: Handshake,
  classification: Layers,
  entities: Network,
  people: IdCard,
  allocations: CalendarClock,
  leave: Palmtree,
  leave_admin: CalendarCog,
  attendance: Clock,
  attendance_team: ClipboardClock,
  performance: Gauge,
  performance_reviews: ClipboardCheck,
  service_contracts: FileSignature,
  registry: BookMarked,
  registry_proposals: Inbox,
};

/** The rail state is a per-device choice (a laptop wants the space, a wide monitor does not), so it lives in the browser, not in user preferences. */
const RAIL_KEY = 'mnc.sidebar.rail';
const readRail = () => {
  try {
    return localStorage.getItem(RAIL_KEY) === '1';
  } catch {
    return false;
  }
};

export function Layout() {
  const { t, i18n } = useTranslation();
  const { me, config, logout, setLocale, setTheme } = useAuth();
  const [open, setOpen] = useState(false);
  const [rail, setRail] = useState(readRail);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locale = i18n.language;
  const toggleRail = () => {
    const next = !rail;
    setRail(next);
    try {
      localStorage.setItem(RAIL_KEY, next ? '1' : '0');
    } catch {
      // Storage blocked: the choice simply lasts for this visit.
    }
  };

  const unread = useQuery({
    queryKey: ['notifications', 'badge'],
    queryFn: () => api.get<NotificationList>('/api/notifications?page_size=1&unread_only=true'),
    refetchInterval: 30_000,
    enabled: Boolean(me) && !me?.must_change_password,
  });

  useEffect(() => setOpen(false), [pathname]);

  if (!me || !config) return null;
  const name = pick(locale, me.display_name_en, me.display_name_ar);
  const instanceName = pick(locale, config.display_name_en, config.display_name_ar);

  // One item is active: the longest route that prefixes the path on a segment boundary, so /stakeholders/programs lights up programs, not stakeholders too.
  let activeCode: string | undefined;
  let activeLength = -1;
  for (const section of config.navigation) {
    for (const item of section.items) {
      const hit = item.route === '/' ? pathname === '/' : pathname === item.route || pathname.startsWith(`${item.route}/`);
      if (hit && item.route.length > activeLength) {
        activeCode = item.code;
        activeLength = item.route.length;
      }
    }
  }

  /** `asRail` = icons only (labels stay for screen readers and as tooltips); the mobile drawer always shows the full menu. */
  const renderNav = (asRail: boolean) => (
    <nav className={cn('flex flex-col p-3', asRail ? 'gap-2 px-2' : 'gap-4')}>
      {config.navigation.map((section, i) => (
        <div key={section.code} className={cn(asRail && i > 0 && 'border-t border-neutral-200 pt-2 dark:border-neutral-800')}>
          {!asRail && <div className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">{pick(locale, section.name_en, section.name_ar)}</div>}
          {section.items.map((item) => {
            const Icon = ICONS[item.icon ?? ''] ?? List;
            const active = item.code === activeCode;
            const label = pick(locale, item.name_en, item.name_ar);
            const unreadCount = item.code === 'notifications' ? (unread.data?.unread_count ?? 0) : 0;
            return (
              <Link key={item.code} to={item.route} title={asRail ? label : undefined} className={cn('relative flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm', asRail && 'justify-center py-2', active ? 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-700/30 dark:text-brand-100' : 'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800')}>
                <Icon size={asRail ? 18 : 16} className="shrink-0" />
                <span className={cn(asRail && 'sr-only')}>{label}</span>
                {unreadCount > 0 && (asRail ? <span className="absolute end-1.5 top-1 h-2 w-2 rounded-full bg-brand-600" /> : <span className="ms-auto rounded-full bg-brand-600 px-1.5 text-[10px] text-white">{unreadCount}</span>)}
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen">
      <aside className={cn('no-print hidden shrink-0 border-e border-neutral-200 bg-white transition-[width] duration-150 dark:border-neutral-800 dark:bg-neutral-900 md:block', rail ? 'w-14' : 'w-60')}>
        <div className={cn('flex items-center gap-2 border-b border-neutral-200 py-3 dark:border-neutral-800', rail ? 'justify-center px-2' : 'px-4')} title={rail ? instanceName : undefined}>
          {config.logo_small_url ? (
            <div className="h-8 w-8 shrink-0 overflow-hidden">
              <img src={config.logo_small_url} alt="" className="max-h-full max-w-full object-contain" />
            </div>
          ) : (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white" style={{ background: config.primary_color }}>
              {instanceName.slice(0, 1)}
            </div>
          )}
          {!rail && <div className="truncate text-sm font-semibold">{instanceName}</div>}
        </div>
        {renderNav(rail)}
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setOpen(false)}>
          <aside className="h-full w-64 overflow-y-auto bg-white dark:bg-neutral-900" onClick={(e) => e.stopPropagation()}>
            {renderNav(false)}
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print flex items-center justify-between gap-2 border-b border-neutral-200 bg-white px-4 py-2 dark:border-neutral-800 dark:bg-neutral-900">
          <div className="flex items-center gap-2">
            <button type="button" className="btn-secondary px-2 py-1 md:hidden" onClick={() => setOpen(true)} aria-label={t('nav.toggleMenu')}>
              <Menu size={18} />
            </button>
            <button type="button" className="btn-secondary hidden px-2 py-1 md:inline-flex" onClick={toggleRail} aria-pressed={rail} aria-label={t(rail ? 'nav.expandMenu' : 'nav.collapseMenu')} title={t(rail ? 'nav.expandMenu' : 'nav.collapseMenu')}>
              {rail ? <PanelLeftOpen size={18} className="rtl:-scale-x-100" /> : <PanelLeftClose size={18} className="rtl:-scale-x-100" />}
            </button>
            <span className="text-sm text-neutral-500 md:hidden">{instanceName}</span>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" className="btn-secondary px-2 py-1" onClick={() => void setLocale(locale === 'ar' ? 'en' : 'ar')} aria-label={t('nav.language')} title={t('nav.language')}>
              <Globe size={16} />
              <span className="text-xs">{locale === 'ar' ? 'EN' : 'ع'}</span>
            </button>
            <ThemeButton onChange={(th) => void setTheme(th)} current={me.theme} />
            <Link to="/notifications" className="btn-secondary relative px-2 py-1" aria-label={t('notifications.title')}>
              <Bell size={16} />
              {(unread.data?.unread_count ?? 0) > 0 && <span className="absolute -top-1 -end-1 h-2 w-2 rounded-full bg-red-500" />}
            </Link>
            <div className="ms-2 flex items-center gap-2">
              <div className="h-8 w-8 shrink-0 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
                {me.avatar_url && <img src={me.avatar_url} alt="" className="max-h-full max-w-full object-cover" />}
              </div>
              <Link to="/account" className="hidden rounded-lg px-1 text-sm leading-tight hover:bg-neutral-100 sm:block dark:hover:bg-neutral-800" title={t('account.title')}>
                <div className="font-medium">{name}</div>
                <div className="text-xs text-neutral-500">{me.groups.join(', ')}</div>
              </Link>
              <button type="button" className="btn-secondary px-2 py-1" onClick={() => void logout()} aria-label={t('nav.signOut')} title={t('nav.signOut')}>
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function ThemeButton({ current, onChange }: { current: 'light' | 'dark' | 'system'; onChange: (t: 'light' | 'dark' | 'system') => void }) {
  const { t } = useTranslation();
  const order = ['light', 'dark', 'system'] as const;
  const next = order[(order.indexOf(current) + 1) % order.length]!;
  const Icon = current === 'light' ? Sun : current === 'dark' ? Moon : Monitor;
  return (
    <button type="button" className="btn-secondary px-2 py-1" onClick={() => onChange(next)} aria-label={t('nav.theme')} title={`${t('nav.theme')}: ${t(`nav.${current}`)}`}>
      <Icon size={16} />
    </button>
  );
}
